/* ===========================================================
   Lucraa — Landing · mockups REALISTAS de produto (peças reusáveis)
   Cada mock renderiza com dados do mock via contrato (nada hardcoded)
   e é composto pelas seções da landing (index.tsx): ComposicaoLucroMock
   (cascata · #metodo), CampanhasMock + ProdutosMock (#campanhas) e
   MonitoresMock (#diagnostico).
   =========================================================== */
import { useState, type CSSProperties } from "react";
import type {
  Campanha,
  LandingData,
  Monitor,
  ProdutoResumo,
  VinculoCampanha,
  WaterfallStep,
} from "../../api/types";
import { fmt } from "../../domain/format";
import { Icon, IntegrationGlyph } from "../../components/ui.jsx";

const F = fmt;

/* provider da campanha → glyph de integração (linhas do MER · ROAS) */
const PROVIDER_GLYPH: Record<Campanha["provider"], string> = {
  meta_ads: "meta",
  google_ads: "gads",
};

const fmtVal = (v: number) => `${v < 0 ? "−" : ""}${F.brl(Math.abs(v))}`;
const fmtCurto = (v: number) => `${v < 0 ? "−" : ""}${F.brl(Math.abs(v)).replace("R$ ", "")}`;
const roasFmt = F.roas;

/* selos espelhados de screens/marketing — mantidos locais p/ a landing não
   depender da superfície de marketing (decisions.md §18). */
const VINCULO_LABEL: Record<VinculoCampanha, string> = {
  gclid: "auto · gclid",
  utm_id: "auto · utm_id",
  auto_append: "auto · id da Meta",
};
const FONTE_GASTO_LABEL: Record<ProdutoResumo["fonteGastoAds"], string> = {
  catalogo: "catálogo",
  destino_anuncio: "destino do anúncio",
};

/* ============================ MÓDULO 1 · MEU LUCRO ============================ */

/** Explicação de cada degrau, em linguagem de lojista — espelha os TERMOS da
 *  tela de Lucro e a cascata de docs/calculo-composicao-lucro.md. */
const EXPLICACAO: Record<string, string> = {
  "Receita bruta": "Tudo que entrou em vendas no período — antes de qualquer dedução.",
  Descontos: "Cupons e abatimentos saem primeiro. Não são custo: é receita da qual você abriu mão.",
  CMV: "Custo da Mercadoria Vendida — quanto você pagou pelos produtos que saíram no período.",
  Frete:
    "O frete que a loja bancou. No frete grátis, entra o custo cheio; quando o cliente paga, não conta.",
  Imposto: "O imposto sobre as vendas do período, pela alíquota que você configura por produto.",
  Embalagens:
    "O custo de embalar cada pedido — caixa, plástico, etiqueta. Custo variável, sai antes da margem.",
  Devoluções: "Pedidos devolvidos no período — estorno da receita e custo da logística reversa.",
  "Receita líquida": "A receita já sem os descontos — a base sobre a qual todos os custos incidem.",
  "Margem de contribuição":
    "O que sobra para pagar a mídia e os custos fixos — e a base do seu ROAS de equilíbrio.",
  Anúncios: "O gasto com mídia paga (Meta e Google) no período.",
  "Lucro das vendas":
    "O que sobrou depois dos custos variáveis e da mídia. Ainda antes dos custos fixos do negócio.",
};

/** Geometria das barras: cada passo "sub" flutua a partir do acumulado; base e
 *  marcos (subtotal/total) nascem do zero. Espelha o Waterfall da tela de Lucro. */
function barrasComposicao(steps: WaterfallStep[]) {
  let running = 0;
  return steps.map((s) => {
    if (s.tipo === "base") {
      running = s.valor;
      return { ...s, bottom: 0, height: s.valor, color: "var(--ink-soft)" };
    }
    if (s.tipo === "total") return { ...s, bottom: 0, height: s.valor, color: "var(--lucro)" };
    if (s.tipo === "subtotal")
      return {
        ...s,
        bottom: 0,
        height: s.valor,
        color: s.rotulo === "Receita líquida" ? "var(--ink-soft)" : "var(--lucro-deep)",
      };
    running += s.valor;
    const color = s.rotulo === "Anúncios" ? "#e6c79a" : "#e6b9b1";
    return { ...s, bottom: running, height: Math.abs(s.valor), color };
  });
}

function corValor(s: WaterfallStep): string {
  if (s.tipo === "total") return "var(--lucro)";
  if (s.tipo === "subtotal") return "var(--lucro-deep)";
  return s.valor < 0 ? "var(--prejuizo)" : "var(--ink)";
}

const CHART_H = 212;
/** headroom no topo do gráfico p/ o rótulo de valor caber acima da barra mais alta */
const VAL_SPACE = 28;

/** Estilo inline da barra + a var `--bi` (índice do degrau) que escalona o
 *  atraso do crescimento na entrada (ver `lpCascGrow` em lucraa.css). */
type FillStyle = CSSProperties & { "--bi": number };

/** Mockup interativo "Composição do lucro" — full-width: a cascata fica à
 *  esquerda (cada barra é um botão) e um inspetor à direita explica o degrau
 *  selecionado. Clique numa barra ou use os controles p/ auditar de onde sai o
 *  lucro. As barras crescem ao revelar a seção (`.lp-casc-wrap.is-in` — ver
 *  reveal.ts + lucraa.css); sem JS / reduced-motion já nascem no tamanho final. */
export function ComposicaoLucroMock({ steps, loja }: { steps: WaterfallStep[]; loja: string }) {
  const [etapa, setEtapa] = useState(0);
  const atual = steps[etapa];
  if (atual == null) return null;
  const barras = barrasComposicao(steps);
  const base = steps.find((s) => s.tipo === "base");
  const max = base?.valor ?? Math.max(...steps.map((s) => Math.abs(s.valor)));
  const total = steps.length;

  return (
    <div className="card lp-card lp-casc-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span className="pill pill-lucro">Composição do lucro</span>
        <span className="pill pill-line" style={{ marginLeft: "auto", fontSize: 11.5 }}>
          {loja} · últimos 30 dias
        </span>
      </div>
      <p className="lp-casc-hint">
        <span className="lp-casc-hint-dot" aria-hidden="true" />
        Clique em cada degrau para ver de onde sai o seu lucro.
      </p>

      <div className="lp-casc">
        {/* cascata — cada degrau é um botão clicável */}
        <div className="lp-casc-plot" role="group" aria-label="Degraus da composição do lucro">
          <div className="lp-casc-bars">
            {barras.map((s, i) => {
              const ativo = i === etapa;
              const h = (s.height / max) * (CHART_H - VAL_SPACE);
              const b = (s.bottom / max) * (CHART_H - VAL_SPACE);
              const topo = b + h; // topo da barra: o valor flutua logo acima dela
              const fillStyle: FillStyle = {
                bottom: b,
                height: Math.max(h, 3),
                background: s.color,
                "--bi": i,
              };
              const valStyle: CSSProperties = ativo
                ? { bottom: topo + 5, color: corValor(s) }
                : { bottom: topo + 5 };
              return (
                <button
                  key={s.rotulo}
                  type="button"
                  className={"lp-casc-bar" + (ativo ? " is-active" : "")}
                  onClick={() => setEtapa(i)}
                  aria-pressed={ativo}
                  aria-label={`${s.rotulo}: ${fmtVal(s.valor)}`}
                >
                  <div className="lp-casc-col" style={{ height: CHART_H }}>
                    <span className="lp-casc-val num" style={valStyle}>
                      {fmtCurto(s.valor)}
                    </span>
                    <div className="lp-casc-fill" style={fillStyle} />
                  </div>
                  <span className="lp-casc-name">{s.rotulo}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* inspetor do degrau selecionado — anima a cada troca (key={etapa}) */}
        <div className="lp-casc-panel" aria-live="polite">
          <div className="lp-casc-panel-body" key={etapa}>
            <span className="lp-casc-step-pill">
              Degrau {etapa + 1} de {total}
            </span>
            <div className="lp-casc-panel-name">
              <span style={{ fontWeight: 700, fontSize: 16 }}>{atual.rotulo}</span>
              <span
                className="num"
                style={{ marginLeft: "auto", fontWeight: 700, fontSize: 22, color: corValor(atual) }}
              >
                {fmtVal(atual.valor)}
              </span>
            </div>
            <p style={{ fontSize: 13.5, color: "var(--ink-soft)", margin: "10px 0 0", lineHeight: 1.5 }}>
              {EXPLICACAO[atual.rotulo] ?? ""}
            </p>
          </div>
          <div className="lp-casc-ctrl">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setEtapa((e) => Math.max(0, e - 1))}
              disabled={etapa === 0}
              aria-label="Degrau anterior"
              style={{ flex: "none" }}
            >
              <Icon
                name="arrowRight"
                size={14}
                color="var(--ink-soft)"
                style={{ transform: "rotate(180deg)" }}
              />{" "}
              <span className="lp-ctrl-txt">Anterior</span>
            </button>
            <div className="lp-casc-dots">
              {steps.map((s, i) => (
                <button
                  key={s.rotulo}
                  type="button"
                  aria-label={`Ir para ${s.rotulo}`}
                  onClick={() => setEtapa(i)}
                  style={{
                    width: i === etapa ? 22 : 8,
                    height: 8,
                    borderRadius: 99,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    background: i === etapa ? "var(--lucro)" : "var(--linha)",
                    transition: "width .2s ease, background .2s ease",
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setEtapa((e) => Math.min(total - 1, e + 1))}
              disabled={etapa === total - 1}
              aria-label="Próximo degrau"
              style={{ flex: "none" }}
            >
              <span className="lp-ctrl-txt">Próximo</span>{" "}
              <Icon name="arrowRight" size={14} color="#fff" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================ MÓDULO 2 · DIAGNÓSTICO ============================ */

export function MonitoresMock({ monitores }: { monitores: Monitor[] }) {
  const visiveis = monitores.slice(0, 3);
  return (
    <div className="lp-grid-3 lp-stagger">
      {visiveis.map((m) => (
        <div
          key={m.id}
          className="card lp-card"
          style={{ padding: 20, display: "flex", flexDirection: "column" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "var(--prejuizo-wash)",
                display: "grid",
                placeItems: "center",
                flex: "none",
              }}
            >
              <Icon name={m.icone} size={16} color="var(--prejuizo)" />
            </div>
            <span className="pill pill-line">Monitor · {m.tag}</span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--prejuizo)",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 99,
                  background: "var(--prejuizo)",
                  display: "inline-block",
                }}
              />
              Alerta ativo
            </span>
            <span className="num" style={{ marginLeft: "auto", textAlign: "right" }}>
              <span style={{ fontSize: 19, fontWeight: 700, color: "var(--prejuizo)" }}>
                {F.brl(m.valorRisco)}
              </span>
              <span style={{ display: "block", fontSize: 11.5, color: "var(--ink-soft)" }}>
                {m.valorLabel}
              </span>
            </span>
          </div>
          <h3 style={{ fontSize: 16, margin: "14px 0 0", lineHeight: 1.3 }}>{m.titulo}</h3>
          <p
            style={{ fontSize: 13, color: "var(--ink-soft)", margin: "6px 0 14px", lineHeight: 1.45 }}
          >
            {m.resumo}
          </p>
          {/* marginTop:auto empurra o "Como resolver" pro rodapé — as 3 caixas
              alinham embaixo; o 14px no <p> garante o respiro mínimo acima */}
          <div className="lp-note lp-note--mint" style={{ marginTop: "auto", fontSize: 13 }}>
            <span
              className="label-eyebrow"
              style={{ color: "var(--lucro-deep)", fontSize: 10.5, display: "block" }}
            >
              Como resolver
            </span>
            <span style={{ display: "block", marginTop: 6, color: "var(--ink)" }}>{m.insight}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================ MÓDULO 3 · MER · ROAS ============================ */

export function CampanhasMock({ ads }: { ads: LandingData["ads"] }) {
  const linhas: Campanha[] = ads.campanhas;
  return (
    <div className="card lp-card" style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 18px",
          borderBottom: "1px solid var(--linha)",
          flexWrap: "wrap",
        }}
      >
        <span className="label-eyebrow" style={{ fontSize: 11 }}>
          Campanhas · lucro das vendas
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--ink-soft)" }}>
          janela 7 dias clique · 1 dia visualização
        </span>
      </div>
      {linhas.map((c) => {
        const lucra = c.lucroLiquido >= 0;
        return (
          <div
            key={c.externalId}
            style={{
              display: "grid",
              gridTemplateColumns: "auto minmax(0,1fr) auto auto",
              gap: 12,
              alignItems: "center",
              padding: "13px 18px",
              borderBottom: "1px solid var(--linha-soft)",
            }}
          >
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}
              title={c.plataforma}
            >
              <IntegrationGlyph kind={PROVIDER_GLYPH[c.provider]} size={20} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c.nome}
              </span>
              <span
                style={{
                  display: "inline-block",
                  marginTop: 4,
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: "var(--lucro-deep)",
                  background: "var(--mint-wash)",
                  borderRadius: 99,
                  padding: "1px 8px",
                }}
              >
                {VINCULO_LABEL[c.vinculo]}
              </span>
            </span>
            <span className="num" style={{ textAlign: "right" }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{roasFmt(c.roasReal)}</span>
              <span style={{ display: "block", fontSize: 11, color: "var(--ink-soft)" }}>
                ROAS real
              </span>
            </span>
            <span className="num" style={{ textAlign: "right", minWidth: 84 }}>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: lucra ? "var(--lucro-deep)" : "var(--prejuizo)",
                }}
              >
                {F.brl(c.lucroLiquido, { sign: true })}
              </span>
              <span style={{ display: "block", fontSize: 11, color: "var(--ink-soft)" }}>
                lucro/mês
              </span>
            </span>
          </div>
        );
      })}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 18px",
          fontSize: 12.5,
          color: "var(--ink-soft)",
          background: "var(--canvas)",
        }}
      >
        <Icon name="help" size={13} color="var(--ink-soft)" />
        <span style={{ flex: 1 }}>Gasto sem campanha identificada — contabilizado no canal</span>
        <span className="num" style={{ fontWeight: 600, color: "var(--ink)" }}>
          {F.brl(ads.gastoSemCampanha)}
        </span>
      </div>
    </div>
  );
}

function acaoDe(p: ProdutoResumo): { txt: string; tom: string; wash: string } {
  if (p.roasReal < p.be)
    return { txt: "Pausar mídia", tom: "var(--prejuizo)", wash: "var(--prejuizo-wash)" };
  if (p.roasReal < p.be * 1.5)
    return { txt: "Ajustar lance", tom: "#7a4a12", wash: "var(--alerta-wash)" };
  return { txt: "Escalar", tom: "var(--lucro-deep)", wash: "var(--mint-wash)" };
}

export function ProdutosMock({ produtos }: { produtos: ProdutoResumo[] }) {
  const linhas = [...produtos].sort((a, b) => b.faturamento - a.faturamento).slice(0, 4);
  const COLS = "minmax(0,1.6fr) auto auto auto 96px";
  return (
    <div
      className="card lp-card"
      style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: COLS,
          gap: 10,
          padding: "12px 18px",
          borderBottom: "1px solid var(--linha)",
          background: "var(--canvas)",
        }}
      >
        {["Produto", "Margem", "BE", "ROAS real", "Ação"].map((h, i) => (
          <span
            key={h}
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "var(--ink-soft)",
              textAlign: i === 0 ? "left" : "right",
            }}
          >
            {h}
          </span>
        ))}
      </div>
      {linhas.map((p) => {
        const a = acaoDe(p);
        return (
          <div
            key={p.nome}
            style={{
              display: "grid",
              gridTemplateColumns: COLS,
              gap: 10,
              alignItems: "center",
              padding: "12px 18px",
              borderBottom: "1px solid var(--linha-soft)",
              flex: 1,
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.nome}
              </span>
              <span style={{ fontSize: 11, color: "var(--ink-soft)" }}>
                mídia {FONTE_GASTO_LABEL[p.fonteGastoAds]}
              </span>
            </span>
            <span className="num" style={{ textAlign: "right", fontSize: 13.5 }}>
              {F.pct(p.margem, { dec: 0 })}
            </span>
            <span
              className="num"
              style={{ textAlign: "right", fontSize: 13.5, color: "var(--ink-soft)" }}
            >
              {roasFmt(p.be)}
            </span>
            <span
              className="num"
              style={{
                textAlign: "right",
                fontSize: 13.5,
                fontWeight: 700,
                color: p.roasReal < p.be ? "var(--prejuizo)" : "var(--lucro-deep)",
              }}
            >
              {roasFmt(p.roasReal)}
            </span>
            <span style={{ textAlign: "right" }}>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: a.tom,
                  background: a.wash,
                  borderRadius: 99,
                  padding: "3px 10px",
                  whiteSpace: "nowrap",
                }}
              >
                {a.txt}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

